import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Label, LanguageService } from '@services/public';
import {
	HTTP_CACHE_MAX_AGE,
	HTTP_REQUEST_KEY,
	httpExpireRequests,
} from '@util';
import { config } from 'environment';
import { ExposedPromise } from 'jakapa-utilities-lite';
import { Observable } from 'rxjs';

// Cache expiration in milliseconds.
const CACHE_EXPIRATION = 300000;

// Organization metadata
export interface Organization {
	uuid: string;
	name: string;
	leader?: boolean;
	addUser?: boolean;
	createGroup?: boolean;
}

export const NO_ORGANIZATIONS: Organization = {
	uuid: null,
	name: '',
};

@Injectable({
	providedIn: 'root',
})
export class OrganizationService {
	private readonly _key: symbol = Symbol();

	// Page langauge.
	labels: { [key in Label]?: string } = {
		[Label.NO_ORGANIZATIONS]: '',
	};

	constructor(
		private _http: HttpClient,
		private _languageSvc: LanguageService,
	) {
		// Get page language.
		this._languageSvc.getLabels(this.labels);
		NO_ORGANIZATIONS.name = this.labels[Label.NO_ORGANIZATIONS];
	}

	clearCache(): void {
		httpExpireRequests(this._key);
	}

	/**
	 * Retrieves a list of all the organizations the user can see. Cached. Always
	 * returns an array with at least 1 organization. If there is an error or if
	 * no organization matches the filter, then an array with the NO_ORGANIZATIONS
	 * organization will be returned.
	 *
	 * @param filter A filter function for the organizations.
	 *
	 * @returns An array of organizations.
	 */
	getOrganizations(
		filter: (organization: Organization) => boolean = () => true,
	): Promise<Array<Organization>> {
		const promise = new ExposedPromise<Array<Organization>>();
		this._http
			.get<Array<Organization>>(`${config.apiBase}member/organization/list`, {
				context: new HttpContext()
					.set(HTTP_REQUEST_KEY, this._key)
					.set(HTTP_CACHE_MAX_AGE, CACHE_EXPIRATION),
			})
			.subscribe({
				next: (res: Array<Organization>): void => {
					const organizations = res.filter(filter);
					if (organizations.length === 0) organizations.push(NO_ORGANIZATIONS);
					promise.resolve(organizations);
				},
				error: (): void => {
					promise.resolve([NO_ORGANIZATIONS]);
				},
			});
		return promise.toPromise();
	}

	/**
	 * Retrieves a list of all the organizations the user has access to. Cached.
	 *
	 * @returns {Array<Organization>} A list of organizations.
	 */
	getOrganizationsAPI(): Observable<Array<Organization>> {
		return this._http.get<Array<Organization>>(
			`${config.apiBase}member/organization/list`,
			{
				context: new HttpContext()
					.set(HTTP_REQUEST_KEY, this._key)
					.set(HTTP_CACHE_MAX_AGE, CACHE_EXPIRATION),
			},
		);
	}

	getLeaderOrganizations(): Observable<any> {
		return this._http.get<any>(`${config.apiBase}leader/organization/list`);
	}

	viewOrganizations(uuid): Observable<any> {
		return this._http.get<any>(
			`${config.apiBase}account/organization/view?uuid=${uuid}`,
		);
	}

	acceptOrganization(reqBody): Observable<any> {
		return this._http.post<any>(
			`${config.apiBase}account/organization/accept`,
			{
				uuid: reqBody,
			},
		);
	}

	declineOrganization(reqObj): Observable<any> {
		return this._http.delete<any>(
			`${config.apiBase}account/organization/decline`,
			{
				body: { uuid: reqObj },
			},
		);
	}
}
